import { installedItems } from "@p0security/shared/install/installed";
import type { McpIntegration } from "@p0security/shared/integrations/resources/mcp/components";
import { useFirestoreDoc } from "providers/FirestoreProvider";
import { useMemo } from "react";

/**
 * One managed MCP server as rendered by the AI inventory UI. Sourced from
 * `o/{tenantId}/integrations/mcp.server.<id>` via Firestore directly —
 * Firestore rules already permit any tenant member to read integration
 * docs, so no backend round-trip is required.
 */
export type ManagedMcpServer = {
  readonly id: string;
  readonly label: string;
  readonly path: string;
  readonly state: string;
  readonly type: string;
  readonly upstream?: string;
  readonly url?: string;
  readonly image?: string;
};

/**
 * Subscribes (live) to the tenant's MCP integration doc and projects each
 * installed server into the shape the inventory UI consumes. Empty list
 * while the doc is loading or absent.
 */
export const useManagedMcpServers = (): {
  servers: ManagedMcpServer[];
  loading: boolean;
} => {
  const { doc: mcpDoc, loading } = useFirestoreDoc<McpIntegration>(
    `integrations/mcp`,
    { live: true, tenantAware: true }
  );

  const servers = useMemo<ManagedMcpServer[]>(() => {
    const config = mcpDoc?.data;
    if (!config) return [];
    const gateways = installedItems("gateway", config);
    return Object.entries(installedItems("server", config)).flatMap(
      ([id, server]) => {
        if (!server) return [];
        const gateway = gateways[server.gateway];
        const hosting = server.hosting;
        return [
          {
            id,
            label: server.label ?? id,
            path: server.path,
            state: server.state,
            type: hosting.type,
            upstream: hosting.type === "upstream" ? hosting.url : undefined,
            image: hosting.type === "container" ? hosting.image : undefined,
            url: gateway?.url ? `${gateway.url}/${server.path}` : undefined,
          },
        ];
      }
    );
  }, [mcpDoc]);

  return { servers, loading };
};
